import { stringify } from 'qs';
import { ReviveDateTime } from './JSON';

export interface IParams {
    [key: string]: any;
}

export interface IConfig {
    method?: string;
    data?: string | {};
    headers?: Headers;
    params?: IParams;
    signal?: RequestInit['signal'];
    cache?: boolean;
    timeout?: number;
}
interface IHeaderConfigObject {
    headers: Headers;
    accessToken?: string;
}

export interface IAccessTokenStore {
    getAccessToken(): Promise<string | undefined>;
}

export class HttpClient {
    private static defaultConfig: IConfig = {
        headers: new Headers(),
    };

    public async get<T>(url: string, config: IConfig = HttpClient.defaultConfig): Promise<T | undefined> {
        const { headers } = await this.setHeaders(config, 'get');

        const requestInit: RequestInit = {
            cache: 'default',
            credentials: 'same-origin',
            headers,
            method: 'GET',
            mode: 'cors',
            signal: config.signal,
        };

        url = url + this.parseParams(config.params);

        return this.exec<T>(url, requestInit, config);
    }

    public async post<T>(url: string, data?: any, config: IConfig = HttpClient.defaultConfig): Promise<T | undefined> {
        const { headers } = await this.setHeaders(config);

        const requestInit: RequestInit = {
            body: data ? JSON.stringify(data) : null,
            cache: 'default',
            credentials: 'same-origin',
            headers,
            method: 'POST',
            mode: 'cors',
            signal: config.signal,
        };

        url = url + this.parseParams(config.params);

        return this.exec<T>(url, requestInit, config);
    }

    public async put<T>(url: string, data?: any, config: IConfig = HttpClient.defaultConfig): Promise<T | undefined> {
        const { headers } = await this.setHeaders(config);

        const requestInit: RequestInit = {
            body: data ? JSON.stringify(data) : null,
            cache: 'default',
            credentials: 'same-origin',
            headers,
            method: 'PUT',
            mode: 'cors',
            signal: config.signal,
        };

        url = url + this.parseParams(config.params);

        return this.exec<T>(url, requestInit, config);
    }

    public async delete<T>(url: string, config: IConfig = HttpClient.defaultConfig): Promise<T | undefined> {
        const { headers } = await this.setHeaders(config);

        const requestInit: RequestInit = {
            cache: 'default',
            credentials: 'same-origin',
            headers,
            method: 'DELETE',
            mode: 'cors',
            signal: config.signal,
        };

        url = url + this.parseParams(config.params);

        return this.exec<T>(url, requestInit, config);
    }

    public setAccessTokenStore(accessTokenStore: IAccessTokenStore) {
        this.accessTokenStore = accessTokenStore;
    }

    public setCsrfToken(csrfToken: string | undefined) {
        this.csrfToken = csrfToken;
    }

    private async setHeaders(
        config: IConfig = HttpClient.defaultConfig,
        method?: string
    ): Promise<IHeaderConfigObject> {
        const headers = config.headers ? new Headers(config.headers) : new Headers();
        const accessToken = this.accessTokenStore && (await this.accessTokenStore.getAccessToken());
        if (accessToken) headers.append('Authorization', 'Bearer ' + accessToken);
        if (this.csrfToken) headers.append('X-CSRF-TOKEN', this.csrfToken);
        if (method?.toLowerCase() !== 'get') headers.append('Content-Type', 'application/json');
        return { headers, accessToken };
    }

    private parseParams(params?: IParams) {
        if (params) {
            const query = stringify(params);
            if (query.length) {
                return '?' + query;
            }
        }
        return '';
    }

    private exec<T>(
        url: string,
        requestInit: RequestInit,
        config: IConfig,
        repeat: boolean = false
    ): Promise<T | undefined> {
        let promise: Promise<T | undefined>;

        if (config.cache) {
            promise = this.cache.get<T>(url, requestInit);

            if (promise) return promise;
        }

        promise = this.fetchAsync<T>(url, requestInit, config, repeat);

        if (config.cache) {
            this.cache.set(url, requestInit, promise);
        }

        return promise;
    }

    private async fetchAsync<T>(
        url: string,
        requestInit: RequestInit,
        config: IConfig,
        repeat: boolean = false
    ): Promise<T | undefined> {
        const { timeout } = config;
        const fetchConfig = { ...requestInit };
        const abortSignal = AbortSignal as any;
        if (timeout) fetchConfig.signal = abortSignal.timeout(timeout);
        const response = await fetch(url, fetchConfig);
        return this.parseOrThrowAsync<T>(response, { requestInit, url, config }, repeat);
    }

    private async parseOrThrowAsync<T>(
        response: Response,
        requestConfig: { url: string; requestInit: RequestInit; config: IConfig },
        repeat: boolean = false
    ) {
        const contentType = response.headers.get('Content-Type');
        const { status } = response;
        if (status === 401 && !repeat) {
            const { url, requestInit, config } = requestConfig;
            const { headers } = await this.setHeaders(config, requestInit.method);
            requestInit.headers = headers;
            await this.exec(url, requestInit, config, true);
            return;
        }
        // if (status === 500) {
        //     if (requestConfig.config.timeout)
        //         // throw new Error(`The request timed out after ${requestConfig.config.timeout/1000} seconds`);
        // }
        const text = await response.text();
        if (contentType?.includes('application/json')) {
            const data = JSON.parse(text, ReviveDateTime);
            if (response.ok) {
                return data as T;
            } else {
                throw data;
            }
        } else {
            if (response.ok) {
                if (text) {
                    throw new Error('Not a valid json response ' + text);
                } else {
                    return;
                }
            } else if (response.status == 429 && !response.statusText.length) {
                throw new Error(`Too many requests. Try again after ${response.headers.get('retry-after')} seconds`);
            } else {
                throw new Error(response.statusText);
            }
        }
    }

    private accessTokenStore?: IAccessTokenStore;
    private csrfToken?: string;
    private cache = new Cache();
}

class Cache {
    public get<T>(url: string, requestInit: RequestInit) {
        const key = this.genKey(url, requestInit);
        return this.cache.get(key) as Promise<T | undefined>;
    }

    public set<T>(url: string, requestInit: RequestInit, response: Promise<T | undefined>) {
        const key = this.genKey(url, requestInit);
        this.cache.set(key, response);
    }

    public delete(url: string, requestInit: RequestInit) {
        const key = this.genKey(url, requestInit);
        this.cache.delete(key);
    }

    private genKey(url: string, requestInit: RequestInit) {
        return JSON.stringify({
            url,
            requestInit,
        });
    }

    private cache = new Map<string, Promise<any>>();
}
